import { useState, useEffect, useCallback, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartLine,
  faSort,
  faSortUp,
  faSortDown,
  faSearch,
  faInfoCircle,
  faArrowRight,
  faTruckFast,
  faChevronLeft,
  faChevronRight,
  faChevronDown,
  faLeaf,
  faRightToBracket,
  faClipboardList,
  faCheck,
  faXmark,
  faClock,
  faExclamationTriangle,
  faUserCheck,
  faBolt,
  faCalendarDays,
} from "@fortawesome/free-solid-svg-icons";
import FormattedDate from "../ui/FormattedDate";
import LastUpdatedBadge from "../ui/LastUpdatedBadge";
import LoadingSpinner from "../ui/LoadingSpinner";
import Skeleton from "../ui/Skeleton";
import { fetchPlatformActivity } from "../../api/adminApi";
import { useDataCache, CACHE_KEYS } from "../../contexts/DataCacheContext";
import { useDebounce } from "../../hooks/useDebounce";

const TABS = [
  { key: "logins", label: "Login History", icon: faRightToBracket },
  { key: "user_usage", label: "User Usage", icon: faUserCheck },
  { key: "listings", label: "Listings", icon: faClipboardList },
  { key: "deliveries", label: "Deliveries", icon: faTruckFast },
];

const DELIVERY_FILTERS = [
  { key: "completed", label: "Completed" },
  { key: "in_transit", label: "In Transit" },
  { key: "overdue", label: "Overdue" },
];

const LISTING_VIEWS = [
  { key: "donor_orgs", label: "Donor Orgs" },
  { key: "receiver_orgs", label: "Receiver Orgs" },
];

const getDatePreset = (key) => {
  const today = new Date();
  const fmt = (d) => d.toISOString().slice(0, 10);
  switch (key) {
    case "7d": {
      const from = new Date(today);
      from.setDate(from.getDate() - 7);
      return { from: fmt(from), to: fmt(today) };
    }
    case "30d": {
      const from = new Date(today);
      from.setDate(from.getDate() - 30);
      return { from: fmt(from), to: fmt(today) };
    }
    case "quarter": {
      const q = Math.floor(today.getMonth() / 3);
      const from = new Date(today.getFullYear(), q * 3, 1);
      return { from: fmt(from), to: fmt(today) };
    }
    case "ytd": {
      const from = new Date(today.getFullYear(), 0, 1);
      return { from: fmt(from), to: fmt(today) };
    }
    default:
      return { from: "", to: "" };
  }
};

const DATE_PRESETS = [
  { key: "all", label: "All Time" },
  { key: "7d", label: "7 Days" },
  { key: "30d", label: "30 Days" },
  { key: "quarter", label: "This Quarter" },
  { key: "ytd", label: "YTD" },
  { key: "custom", label: "Custom" },
];

function PlatformActivitySection({ darkMode, t }) {
  const [activeTab, setActiveTab] = useState("logins");
  const [kpis, setKpis] = useState(null);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, perPage: 20, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 350);
  const [sortConfig, setSortConfig] = useState({ key: "eventDate", direction: "desc" });

  // tab-specific filters
  const [deliveryFilter, setDeliveryFilter] = useState("completed");
  const [listingView, setListingView] = useState("donor_orgs");

  // collapsible date groups in deliveries
  const [collapsedDates, setCollapsedDates] = useState({});

  // date range filter
  const [datePreset, setDatePreset] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // animation key — increments on data change to retrigger row animations
  const [animKey, setAnimKey] = useState(0);

  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  // tracks if KPI cascade should play (only on initial load + manual refresh)
  const [kpiCascade, setKpiCascade] = useState(true);
  const isFetching = useRef(false);
  const pendingLoad = useRef(null);
  const sectionTopRef = useRef(null);

  const { getCachedData, setCachedData } = useDataCache();

  // cache sub-key includes filters so different filter combos don't collide
  const cacheSubKey = (type, page, extra = "") => `${type}_${page}_${extra}`;

  // build extra params based on current tab + filter state
  const buildExtraParams = useCallback((tab, opts = {}) => {
    const params = {};
    if (tab === "deliveries") {
      const f = opts.deliveryFilter !== undefined ? opts.deliveryFilter : deliveryFilter;
      if (f !== "all") params.status = f;
    }
    if (tab === "listings") {
      const v = opts.listingView !== undefined ? opts.listingView : listingView;
      params.view = v;
    }
    // include search term if present
    const s = opts.search !== undefined ? opts.search : debouncedSearch;
    if (s) params.search = s;
    // include date range if set
    const df = opts.dateFrom !== undefined ? opts.dateFrom : dateFrom;
    const dt = opts.dateTo !== undefined ? opts.dateTo : dateTo;
    if (df) params.date_from = df;
    if (dt) params.date_to = dt;
    return params;
  }, [deliveryFilter, listingView, debouncedSearch, dateFrom, dateTo]);

  const loadData = useCallback(
    async (type, page = 1, forceRefresh = false, extraParams = {}) => {
      if (isFetching.current) {
        pendingLoad.current = { type, page, forceRefresh, extraParams };
        setLoading(true);
        return;
      }

      // resolve the api type for org rollup
      let apiType = type;
      if (type === "listings") {
        apiType = "listings_orgs";
      }

      const cacheExtra = JSON.stringify(extraParams);
      if (!forceRefresh) {
        const cached = getCachedData(CACHE_KEYS.ADMIN_PLATFORM_ACTIVITY);
        const subKey = cacheSubKey(apiType, page, cacheExtra);

        if (cached?.data?.[subKey]) {
          const entry = cached.data[subKey];
          setKpis(entry.kpis);
          setRows(entry.rows || []);
          setPagination(entry.pagination || { page: 1, perPage: 20, total: 0, totalPages: 1 });
          setLastUpdated(cached.timestamp);
          setLoading(false);
          setAnimKey((k) => k + 1);
          return;
        }
      }

      isFetching.current = true;

      try {
        if (forceRefresh) {
          setRefreshing(true);
          setKpiCascade(true);
          setLoading(true);
        } else {
          setLoading(true);
        }
        setError(null);

        const params = { type: apiType, page, per_page: 20, ...extraParams };
        // map view to side param for org rollup
        if (apiType === "listings_orgs") {
          params.side = extraParams.view === "receiver_orgs" ? "receiver" : "donor";
          delete params.view;
        }

        const data = await fetchPlatformActivity(params);
        const timestamp = Date.now();

        setKpis(data.kpis);
        setRows(data.rows || []);
        setPagination(data.pagination || { page: 1, perPage: 20, total: 0, totalPages: 1 });
        setLastUpdated(timestamp);
        setAnimKey((k) => k + 1);

        const existing = getCachedData(CACHE_KEYS.ADMIN_PLATFORM_ACTIVITY);
        const cacheMap = existing?.data || {};
        cacheMap[cacheSubKey(apiType, page, cacheExtra)] = {
          kpis: data.kpis,
          rows: data.rows,
          pagination: data.pagination,
        };
        setCachedData(CACHE_KEYS.ADMIN_PLATFORM_ACTIVITY, cacheMap, timestamp);
      } catch (err) {
        console.error("Failed to load platform activity:", err);
        setError(t("Failed to load platform activity"));
      } finally {
        setLoading(false);
        setRefreshing(false);
        isFetching.current = false;
        // disable cascade after animation finishes (~1.6s)
        setTimeout(() => setKpiCascade(false), 1800);

        if (pendingLoad.current) {
          const { type: pt, page: pp, forceRefresh: pf, extraParams: pe } = pendingLoad.current;
          pendingLoad.current = null;
          loadData(pt, pp, pf, pe);
        }
      }
    },
    [t, getCachedData, setCachedData],
  );

  const handleRefresh = useCallback(() => {
    setRows([]);
    const extra = buildExtraParams(activeTab);
    loadData(activeTab, pagination.page, true, extra);
  }, [loadData, activeTab, pagination.page, buildExtraParams]);

  const handlePageChange = useCallback(
    (newPage) => {
      const extra = buildExtraParams(activeTab);
      loadData(activeTab, newPage, false, extra);
      sectionTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    [loadData, activeTab, buildExtraParams],
  );

  const handleTabChange = useCallback(
    (tabKey) => {
      setKpiCascade(false);
      setActiveTab(tabKey);
      setSearchQuery("");
      const defaultSort = tabKey === "user_usage" ? "totalLogins" : "eventDate";
      setSortConfig({ key: defaultSort, direction: "desc" });
      setPagination((p) => ({ ...p, page: 1 }));
      setRows([]);
      setLoading(true);
      // reset sub-filters to defaults when switching tabs
      if (tabKey === "deliveries") {
        setDeliveryFilter("completed");
        prevDeliveryFilter.current = "completed";
      }
      if (tabKey === "listings") {
        setListingView("donor_orgs");
        prevListingView.current = "donor_orgs";
      }
      const extra = buildExtraParams(tabKey, {
        deliveryFilter: "completed",
        listingView: "donor_orgs",
      });
      loadData(tabKey, 1, false, extra);
    },
    [loadData, buildExtraParams],
  );

  // reload when delivery filter changes
  const prevDeliveryFilter = useRef(deliveryFilter);
  useEffect(() => {
    if (activeTab === "deliveries" && prevDeliveryFilter.current !== deliveryFilter) {
      prevDeliveryFilter.current = deliveryFilter;
      setRows([]);
      setPagination((p) => ({ ...p, page: 1 }));
      setLoading(true);
      const extra = buildExtraParams("deliveries");
      loadData("deliveries", 1, false, extra);
    }
  }, [deliveryFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // reload when listing view changes
  const prevListingView = useRef(listingView);
  useEffect(() => {
    if (activeTab === "listings" && prevListingView.current !== listingView) {
      prevListingView.current = listingView;
      setRows([]);
      setPagination((p) => ({ ...p, page: 1 }));
      setLoading(true);
      const extra = buildExtraParams("listings");
      loadData("listings", 1, false, extra);
    }
  }, [listingView]); // eslint-disable-line react-hooks/exhaustive-deps

  // reload when debounced search changes
  const prevSearch = useRef(debouncedSearch);
  useEffect(() => {
    if (prevSearch.current !== debouncedSearch) {
      prevSearch.current = debouncedSearch;
      setRows([]);
      setPagination((p) => ({ ...p, page: 1 }));
      setLoading(true);
      const extra = buildExtraParams(activeTab, { search: debouncedSearch });
      loadData(activeTab, 1, false, extra);
    }
  }, [debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  // handle date preset selection
  const handleDatePreset = useCallback((key) => {
    setDatePreset(key);
    if (key === "all") {
      setDateFrom("");
      setDateTo("");
    } else if (key === "custom") {
      // keep current values, user will set via inputs
    } else {
      const { from, to } = getDatePreset(key);
      setDateFrom(from);
      setDateTo(to);
    }
  }, []);

  // reload when date range changes
  const prevDateFrom = useRef(dateFrom);
  const prevDateTo = useRef(dateTo);
  useEffect(() => {
    if (prevDateFrom.current !== dateFrom || prevDateTo.current !== dateTo) {
      prevDateFrom.current = dateFrom;
      prevDateTo.current = dateTo;
      setRows([]);
      setPagination((p) => ({ ...p, page: 1 }));
      setLoading(true);
      const extra = buildExtraParams(activeTab, { dateFrom, dateTo });
      loadData(activeTab, 1, false, extra);
    }
  }, [dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasInitialLoad = useRef(false);
  useEffect(() => {
    if (!hasInitialLoad.current) {
      hasInitialLoad.current = true;
      loadData("logins", 1);
    }
  }, [loadData]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return faSort;
    return sortConfig.direction === "asc" ? faSortUp : faSortDown;
  };

  // rows are already filtered server-side, just sort client-side
  const filteredRows = [...rows]
    .sort((a, b) => {
      let aVal = a[sortConfig.key] ?? "";
      let bVal = b[sortConfig.key] ?? "";
      if (typeof aVal === "number" && typeof bVal === "number") {
        // keep as numbers
      } else if (typeof aVal === "string") {
        aVal = aVal.toLowerCase();
        bVal = (bVal || "").toLowerCase();
      }
      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

  const fmtNum = (n) => {
    const x = Number(n);
    if (!Number.isFinite(x)) return "\u2014";
    return x.toLocaleString();
  };

  const currentTabConfig = TABS.find((tab) => tab.key === activeTab);

  // reusable helpers
  // KPI drill-down — click a card to jump to the relevant tab + filter
  const drillDown = useCallback((tab, filter = {}) => {
    setKpiCascade(false);
    setActiveTab(tab);
    setSearchQuery("");
    const defaultSort = tab === "user_usage" ? "totalLogins" : "eventDate";
    setSortConfig({ key: defaultSort, direction: "desc" });
    setPagination((p) => ({ ...p, page: 1 }));
    setRows([]);
    setLoading(true);

    if (filter.deliveryFilter) {
      setDeliveryFilter(filter.deliveryFilter);
      prevDeliveryFilter.current = filter.deliveryFilter;
    }
    if (filter.listingView) {
      setListingView(filter.listingView);
      prevListingView.current = filter.listingView;
    }

    const extra = buildExtraParams(tab, filter);
    loadData(tab, 1, false, extra);
  }, [loadData, buildExtraParams]);

  // stat bar item for the compact top bar
  const StatItem = ({ value, label, tooltip, onClick, secondary }) => (
    <div
      className={`kpi-stat${onClick ? " kpi-stat-clickable" : ""}`}
      onClick={onClick}
      onKeyDown={onClick ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div className="kpi-stat-value">{value}</div>
      <div className="kpi-stat-label">
        {label}
        {tooltip && (
          <span className="kpi-tooltip-wrap">
            <FontAwesomeIcon icon={faInfoCircle} className="kpi-info-icon" />
            <span className="kpi-tooltip">{tooltip}</span>
          </span>
        )}
      </div>
      {secondary && <div className="kpi-stat-secondary">{secondary}</div>}
      {onClick && <FontAwesomeIcon icon={faArrowRight} className="kpi-stat-arrow" />}
    </div>
  );

  const FilterBar = ({ filters, active, onChange }) => (
    <div className="activity-filter-bar">
      {filters.map((f) => (
        <button
          key={f.key}
          className={`activity-filter-btn${active === f.key ? " active" : ""}`}
          onClick={() => onChange(f.key)}
        >
          {t(f.label)}
        </button>
      ))}
    </div>
  );

  const SortTh = ({ colKey, label, width }) => (
    <th style={{ width }} className="sortable" onClick={() => handleSort(colKey)}>
      {t(label)}
      <FontAwesomeIcon icon={getSortIcon(colKey)} className="sort-icon" />
    </th>
  );

  // table renderers

  const renderLoginsTable = () => (
    <table className="admin-table">
      <thead>
        <tr>
          <SortTh colKey="eventDate" label="Date" width="15%" />
          <SortTh colKey="userName" label="User" width="25%" />
          <SortTh colKey="userEmail" label="Email" width="25%" />
          <SortTh colKey="ipAddress" label="IP Address" width="20%" />
          <SortTh colKey="success" label="Status" width="15%" />
        </tr>
      </thead>
      <tbody key={animKey}>
        {filteredRows.map((r) => (
          <tr key={r.id}>
            <td><FormattedDate date={r.eventDate} className="cell-date" /></td>
            <td><span className="cell-primary">{r.userName}</span></td>
            <td><span className="cell-secondary">{r.userEmail}</span></td>
            <td><span className="cell-mono">{r.ipAddress}</span></td>
            <td>
              {r.success ? (
                <span className="status-pill status-active">
                  <FontAwesomeIcon icon={faCheck} /> {t("Success")}
                </span>
              ) : (
                <span className="status-pill status-failed">
                  <FontAwesomeIcon icon={faXmark} /> {t("Failed")}
                </span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderUserUsageTable = () => (
    <table className="admin-table">
      <thead>
        <tr>
          <SortTh colKey="userName" label="User" width="17%" />
          <SortTh colKey="orgName" label="Organization" width="15%" />
          <SortTh colKey="email" label="Email" width="17%" />
          <SortTh colKey="totalLogins" label="Total Logins" width="9%" />
          <SortTh colKey="loginsThisMonth" label="This Month" width="9%" />
          <SortTh colKey="loginsThisQuarter" label="This Quarter" width="9%" />
          <SortTh colKey="daysActive" label="Days Active" width="9%" />
          <SortTh colKey="lastLogin" label="Last Login" width="11%" />
        </tr>
      </thead>
      <tbody key={animKey}>
        {filteredRows.map((r) => (
          <tr key={r.userId}>
            <td>
              <span className="cell-primary">{r.userName}</span>
              {!r.isActive && <span className="status-pill status-inactive" style={{ marginLeft: 6, fontSize: 10 }}>{t("Inactive")}</span>}
            </td>
            <td><span className="cell-secondary">{r.orgName}</span></td>
            <td><span className="cell-secondary">{r.email}</span></td>
            <td className="td-center"><span className="cell-count">{fmtNum(r.totalLogins)}</span></td>
            <td className="td-center"><span className="cell-count">{fmtNum(r.loginsThisMonth)}</span></td>
            <td className="td-center"><span className="cell-count">{fmtNum(r.loginsThisQuarter)}</span></td>
            <td className="td-center"><span className="cell-count">{fmtNum(r.daysActive)}</span></td>
            <td>{r.lastLogin ? <FormattedDate date={r.lastLogin} className="cell-date" /> : "\u2014"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderListingsTable = () => {
    const isDonor = listingView === "donor_orgs";
    return (
      <table className="admin-table">
        <thead>
          <tr>
            <SortTh colKey="orgName" label="Organization" width="25%" />
            {isDonor ? (
              <>
                <SortTh colKey="totalListings" label="Listings" width="12%" />
                <SortTh colKey="activeListings" label="Active" width="12%" />
                <SortTh colKey="overdueListings" label="Overdue" width="12%" />
                <SortTh colKey="totalQty" label="Total Qty" width="13%" />
                <SortTh colKey="remainingQty" label="Remaining" width="13%" />
              </>
            ) : (
              <>
                <SortTh colKey="totalRequests" label="Requests" width="12%" />
                <SortTh colKey="pending" label="Pending" width="12%" />
                <SortTh colKey="completed" label="Completed" width="12%" />
                <SortTh colKey="qtyReceived" label="Qty Received" width="15%" />
              </>
            )}
            <SortTh colKey="lastActivity" label="Last Activity" width="13%" />
          </tr>
        </thead>
        <tbody key={animKey}>
          {filteredRows.map((r) => (
            <tr key={r.orgId}>
              <td><span className="cell-primary">{r.orgName}</span></td>
              {isDonor ? (
                <>
                  <td className="td-center"><span className="cell-count">{fmtNum(r.totalListings)}</span></td>
                  <td className="td-center"><span className="cell-count">{fmtNum(r.activeListings)}</span></td>
                  <td className="td-center">
                    <span className={`cell-count${r.overdueListings > 0 ? " cell-overdue" : ""}`}>
                      {fmtNum(r.overdueListings)}
                    </span>
                  </td>
                  <td className="td-center"><span className="cell-count">{fmtNum(r.totalQty)}</span></td>
                  <td className="td-center"><span className="cell-count">{fmtNum(r.remainingQty)}</span></td>
                </>
              ) : (
                <>
                  <td className="td-center"><span className="cell-count">{fmtNum(r.totalRequests)}</span></td>
                  <td className="td-center"><span className="cell-count">{fmtNum(r.pending)}</span></td>
                  <td className="td-center"><span className="cell-count">{fmtNum(r.completed)}</span></td>
                  <td className="td-center"><span className="cell-count">{fmtNum(r.qtyReceived)}</span></td>
                </>
              )}
              <td>{r.lastActivity ? <FormattedDate date={r.lastActivity} className="cell-date" /> : "\u2014"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const renderDeliveriesTable = () => {
    // group rows by date for date header rows
    const grouped = [];
    let lastDate = null;
    let dateCount = {};
    // first pass: count rows per date
    filteredRows.forEach((r) => {
      const dateStr = r.eventDate ? r.eventDate.slice(0, 10) : "Unknown";
      dateCount[dateStr] = (dateCount[dateStr] || 0) + 1;
    });
    // second pass: build grouped list
    filteredRows.forEach((r) => {
      const dateStr = r.eventDate ? r.eventDate.slice(0, 10) : "Unknown";
      if (dateStr !== lastDate) {
        grouped.push({ type: "header", date: dateStr, count: dateCount[dateStr] });
        lastDate = dateStr;
      }
      grouped.push({ type: "row", data: r, date: dateStr });
    });

    const toggleDate = (date) => {
      setCollapsedDates((prev) => ({ ...prev, [date]: !prev[date] }));
    };

    return (
      <table className="admin-table">
        <thead>
          <tr>
            <SortTh colKey="donorName" label="Donor" width="17%" />
            <SortTh colKey="receiverName" label="Receiver" width="17%" />
            <SortTh colKey="material" label="Material" width="16%" />
            <SortTh colKey="quantity" label="Qty (lbs)" width="10%" />
            <SortTh colKey="allocatedCost" label="Revenue" width="10%" />
            <SortTh colKey="latestPickup" label="Deadline" width="14%" />
            <SortTh colKey="status" label="Status" width="16%" />
          </tr>
        </thead>
        <tbody key={animKey}>
          {grouped.map((item, idx) => {
            if (item.type === "header") {
              const isCollapsed = collapsedDates[item.date];
              return (
                <tr
                  key={`hdr-${item.date}`}
                  className="date-group-header"
                  onClick={() => toggleDate(item.date)}
                >
                  <td colSpan={7}>
                    <FontAwesomeIcon
                      icon={faChevronDown}
                      className={`date-group-chevron${isCollapsed ? " collapsed" : ""}`}
                    />
                    <FormattedDate date={item.date} className="date-group-label" />
                    <span className="date-group-count">{item.count}</span>
                  </td>
                </tr>
              );
            }
            if (collapsedDates[item.date]) return null;
            return (
              <tr key={item.data.id}>
                <td>
                  <span className="cell-primary cell-truncate" title={item.data.donorName}>{item.data.donorName}</span>
                  {item.data.donorOrg !== item.data.donorName && <span className="cell-org-sub" title={item.data.donorOrg}>{item.data.donorOrg}</span>}
                </td>
                <td>
                  <span className="cell-primary cell-truncate" title={item.data.receiverName}>{item.data.receiverName}</span>
                  {item.data.receiverOrg !== item.data.receiverName && <span className="cell-org-sub" title={item.data.receiverOrg}>{item.data.receiverOrg}</span>}
                </td>
                <td><span className="cell-primary cell-truncate" title={item.data.material}>{item.data.material}</span></td>
                <td className="td-center"><span className="cell-count">{fmtNum(item.data.quantity)}</span></td>
                <td className="td-center"><span className="cell-count">${fmtNum(item.data.allocatedCost)}</span></td>
                <td>
                  {item.data.latestPickup ? (
                    <span className={item.data.status === "overdue" ? "cell-overdue" : "cell-date"}>
                      <FormattedDate date={item.data.latestPickup} />
                    </span>
                  ) : "\u2014"}
                </td>
                <td>
                  <span className={`status-pill status-${item.data.status}`}>
                    {item.data.status === "completed" && <FontAwesomeIcon icon={faCheck} />}
                    {item.data.status === "overdue" && <FontAwesomeIcon icon={faExclamationTriangle} />}
                    {item.data.status === "in_transit" && <FontAwesomeIcon icon={faClock} />}
                    {" "}{item.data.status === "in_transit" ? t("In Transit") : t(item.data.status.charAt(0).toUpperCase() + item.data.status.slice(1))}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  const TABLE_RENDERERS = {
    logins: renderLoginsTable,
    user_usage: renderUserUsageTable,
    listings: renderListingsTable,
    deliveries: renderDeliveriesTable,
  };

  if (loading && !kpis) {
    return (
      <div className="profile-section admin-section-wide">
        <div className="admin-loading">
          <LoadingSpinner size={40} />
          <span>{t("Loading platform activity...")}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-section admin-section-wide" ref={sectionTopRef}>
      <div className="profile-section-header">
        <div className="profile-section-icon admin-icon">
          <FontAwesomeIcon icon={faChartLine} />
        </div>
        <div style={{ flex: 1 }}>
          <h2 className="profile-section-title">{t("Platform Activity")}</h2>
          <p className="profile-section-desc">
            {t("Overview of platform usage and recent activity")}
          </p>
        </div>
        <div className="header-status-area">
          <LastUpdatedBadge
            timestamp={lastUpdated}
            onRefresh={handleRefresh}
            isRefreshing={refreshing}
            variant="admin"
          />
        </div>
      </div>

      {error && (
        <div className="admin-error">
          <FontAwesomeIcon icon={faInfoCircle} />
          <span>{error}</span>
        </div>
      )}

      {/* kpi — stat bar + detail cards (Option C) */}
      {(!kpis || refreshing) && (
        <div className="kpi-c-wrapper">
          <div className="kpi-stat-bar">
            {[...Array(5)].map((_, i) => (
              <div className="kpi-stat" key={i}>
                <Skeleton width="48px" height="22px" borderRadius="6px" />
                <div style={{ marginTop: 6 }}>
                  <Skeleton width="72px" height="10px" borderRadius="4px" />
                </div>
              </div>
            ))}
          </div>
          <div className="kpi-detail-row">
            {[...Array(3)].map((_, i) => (
              <div className="kpi-detail-card kpi-detail-skeleton" key={i}>
                <div className="kpi-detail-header">
                  <Skeleton width="34px" height="34px" borderRadius="9px" />
                  <Skeleton width="60px" height="12px" borderRadius="4px" />
                </div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
                  <Skeleton width="80px" height="28px" borderRadius="6px" />
                  <div>
                    <Skeleton width="60px" height="10px" borderRadius="4px" />
                    <div style={{ marginTop: 4 }}>
                      <Skeleton width="50px" height="10px" borderRadius="4px" />
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: 12 }}>
                  <Skeleton width="120px" height="20px" borderRadius="12px" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {kpis && !refreshing && (
        <div className={`kpi-c-wrapper${kpiCascade ? " kpi-fade-in" : ""}`}>
          {/* compact stat bar */}
          <div className="kpi-stat-bar">
            <StatItem value={fmtNum(kpis.activeUsers)} label={t("Active Users")} tooltip={t("Users with is_active = 1 and not deleted. Inactive: deactivated accounts.")} onClick={() => drillDown("user_usage")} />
            <StatItem value={fmtNum(kpis.activeOrgs)} label={t("Organizations")} tooltip={t("Active, non-deleted organizations. Each org may have multiple user accounts.")} onClick={() => drillDown("listings", { listingView: "donor_orgs" })} />
            <StatItem value={fmtNum(kpis.activeListings + kpis.otherListings)} label={t("Listings")} tooltip={`${fmtNum(kpis.activeListings)} ${t("active")} + ${fmtNum(kpis.otherListings)} ${t("expired/other. Excludes deleted listings.")}`} onClick={() => drillDown("listings", { listingView: "donor_orgs" })} />
            <StatItem value={fmtNum(kpis.logins24h)} label={t("Logins (24h)")} tooltip={kpis.failedLogins24h > 0 ? `${fmtNum(kpis.failedLogins24h)} ${t("failed attempts in the same period.")}` : t("Successful login events in the past 24 hours. No failed attempts.")} onClick={() => drillDown("logins")} secondary={kpis.failedLogins24h > 0 ? <span className="kpi-stat-warn">{fmtNum(kpis.failedLogins24h)} {t("failed")}</span> : null} />
            <StatItem value={fmtNum(kpis.pendingRequests)} label={t("Pending Requests")} tooltip={t("Requests where active = 1 but not yet allocated by the donor.")} />
          </div>

          {/* 3 detail cards */}
          <div className="kpi-detail-row">
            {/* Pipeline → drills to In Transit (what's moving in the pipeline) */}
            <div className="kpi-detail-card" onClick={() => drillDown("deliveries", { deliveryFilter: "in_transit" })} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); drillDown("deliveries", { deliveryFilter: "in_transit" }); } }} role="button" tabIndex={0}>
              <div className="kpi-detail-header">
                <div className="kpi-detail-icon purple">
                  <FontAwesomeIcon icon={faChartLine} />
                </div>
                <span className="kpi-detail-title">{t("Pipeline")}</span>
                <span className="kpi-tooltip-wrap">
                  <FontAwesomeIcon icon={faInfoCircle} className="kpi-info-icon" />
                  <span className="kpi-tooltip">{t("Fulfillment = completed ÷ (completed + in transit + overdue). In transit = allocated within pickup deadline, not yet delivered.")}</span>
                </span>
              </div>
              <div className="kpi-detail-body">
                <span className="kpi-detail-value">{kpis.fulfillmentPct ?? 0}<span className="kpi-detail-unit">%</span></span>
                <div className="kpi-detail-meta">
                  <span className="kpi-detail-sub">{t("fulfillment rate")}</span>
                  <span className="kpi-detail-sub">{fmtNum(kpis.inTransit)} {t("in transit")}</span>
                </div>
              </div>
              {kpis.overdueDeliveries > 0 && (
                <span className="kpi-detail-pill red" onClick={(e) => { e.stopPropagation(); drillDown("deliveries", { deliveryFilter: "overdue" }); }}>
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  {fmtNum(kpis.overdueDeliveries)} {t("overdue")}
                </span>
              )}
              <FontAwesomeIcon icon={faArrowRight} className="kpi-detail-arrow" />
            </div>

            {/* Impact → drills to Completed deliveries */}
            <div className="kpi-detail-card" onClick={() => drillDown("deliveries", { deliveryFilter: "completed" })} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); drillDown("deliveries", { deliveryFilter: "completed" }); } }} role="button" tabIndex={0}>
              <div className="kpi-detail-header">
                <div className="kpi-detail-icon green">
                  <FontAwesomeIcon icon={faLeaf} />
                </div>
                <span className="kpi-detail-title">{t("Impact")}</span>
                <span className="kpi-tooltip-wrap">
                  <FontAwesomeIcon icon={faInfoCircle} className="kpi-info-icon" />
                  <span className="kpi-tooltip">{t("SUM of allocated quantity (lbs) from all requests where delivery was confirmed (date_delivered is set).")}</span>
                </span>
              </div>
              <div className="kpi-detail-body">
                <span className="kpi-detail-value green">{fmtNum(kpis.totalQtyLbs)}</span>
                <div className="kpi-detail-meta">
                  <span className="kpi-detail-sub">{t("lbs diverted")}</span>
                  <span className="kpi-detail-sub">{t("from landfill")}</span>
                </div>
              </div>
              <span className="kpi-detail-pill green">
                <FontAwesomeIcon icon={faTruckFast} />
                {fmtNum(kpis.totalDeliveries)} {t("deliveries completed")}
              </span>
              <FontAwesomeIcon icon={faArrowRight} className="kpi-detail-arrow" />
            </div>

            {/* Activity → drills to Login History (raw activity events) */}
            <div className="kpi-detail-card" onClick={() => drillDown("logins")} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); drillDown("logins"); } }} role="button" tabIndex={0}>
              <div className="kpi-detail-header">
                <div className="kpi-detail-icon blue">
                  <FontAwesomeIcon icon={faBolt} />
                </div>
                <span className="kpi-detail-title">{t("Activity")}</span>
                <span className="kpi-tooltip-wrap">
                  <FontAwesomeIcon icon={faInfoCircle} className="kpi-info-icon" />
                  <span className="kpi-tooltip">{t("Active = is_active = 1, not deleted. Logins counted from login_history in the past 24h.")}</span>
                </span>
              </div>
              <div className="kpi-detail-body">
                <span className="kpi-detail-value">{fmtNum(kpis.activeUsers)}</span>
                <div className="kpi-detail-meta">
                  <span className="kpi-detail-sub">{t("active users")}</span>
                  <span className="kpi-detail-sub">{t("across")} {fmtNum(kpis.activeOrgs)} {t("orgs")}</span>
                </div>
              </div>
              <span className="kpi-detail-pill blue">
                <FontAwesomeIcon icon={faRightToBracket} />
                {fmtNum(kpis.logins24h)} {t("logins today")}
              </span>
              <FontAwesomeIcon icon={faArrowRight} className="kpi-detail-arrow" />
            </div>
          </div>
        </div>
      )}

      {/* sub-tabs */}
      <div className={`activity-tab-bar${kpiCascade ? " cascade-in" : ""}`}>
        {TABS.map((tab) => (
          <button
            key={tab.key}
            className={`activity-tab-btn ${activeTab === tab.key ? "active" : ""}`}
            onClick={() => handleTabChange(tab.key)}
          >
            <FontAwesomeIcon icon={tab.icon} />
            <span>{t(tab.label)}</span>
            {tab.wip && <span className="wip-badge">WIP</span>}
          </button>
        ))}
      </div>

      {/* toolbar with filters */}
      <div className={`admin-toolbar${kpiCascade ? " cascade-in" : ""}`}>
        <div className="admin-toolbar-left">
          <div className="admin-search-wrap">
            <FontAwesomeIcon icon={faSearch} className="admin-search-icon" />
            <input
              type="text"
              className="admin-search-input"
              placeholder={t(`Search ${currentTabConfig?.label?.toLowerCase() || "activity"}...`)}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {activeTab === "deliveries" && (
            <FilterBar filters={DELIVERY_FILTERS} active={deliveryFilter} onChange={setDeliveryFilter} />
          )}

          {activeTab === "listings" && (
            <FilterBar filters={LISTING_VIEWS} active={listingView} onChange={setListingView} />
          )}
        </div>

        <div className="admin-toolbar-right">
          <div className="date-range-bar">
            <FontAwesomeIcon icon={faCalendarDays} className="date-range-icon" />
            {DATE_PRESETS.map((p) => (
              <button
                key={p.key}
                className={`activity-filter-btn${datePreset === p.key ? " active" : ""}`}
                onClick={() => handleDatePreset(p.key)}
              >
                {t(p.label)}
              </button>
            ))}
            {datePreset === "custom" && (
              <div className="date-range-inputs">
                <input
                  type="date"
                  className="date-range-input"
                  value={dateFrom}
                  onChange={(e) => { setDateFrom(e.target.value); setDatePreset("custom"); }}
                />
                <span className="date-range-sep">—</span>
                <input
                  type="date"
                  className="date-range-input"
                  value={dateTo}
                  onChange={(e) => { setDateTo(e.target.value); setDatePreset("custom"); }}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* table */}
      <div className={`admin-table-container ${refreshing ? "shimmer-overlay" : ""}${kpiCascade ? " cascade-in" : ""}`}>
        {loading ? (
          <div className="admin-loading" style={{ padding: "40px 0" }}>
            <LoadingSpinner size={30} />
          </div>
        ) : (
          <>
            {TABLE_RENDERERS[activeTab]?.()}

            {filteredRows.length === 0 && (
              <div className="admin-empty-state">
                <FontAwesomeIcon icon={
                  activeTab === "logins" ? faRightToBracket :
                  activeTab === "user_usage" ? faUserCheck :
                  activeTab === "listings" ? faClipboardList :
                  activeTab === "deliveries" ? faTruckFast :
                  faChartLine
                } />
                <p>{searchQuery
                  ? t("No results matching") + ` "${searchQuery}"`
                  : activeTab === "logins" ? t("No login activity recorded yet")
                  : activeTab === "user_usage" ? t("No user activity data available")
                  : activeTab === "listings" ? t("No listings found for this view")
                  : activeTab === "deliveries" && deliveryFilter === "overdue" ? t("No overdue deliveries — looking good!")
                  : activeTab === "deliveries" && deliveryFilter === "in_transit" ? t("No deliveries currently in transit")
                  : activeTab === "deliveries" ? t("No completed deliveries yet")
                  : t("No activity found")
                }</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* pagination */}
      <div className="admin-table-footer">
        <span className="table-count">
          {t("Showing")} {filteredRows.length} {t("of")} {pagination.total}
        </span>
        {pagination.totalPages > 1 && (
          <div className="activity-pagination">
            <button
              className="activity-page-btn"
              disabled={pagination.page <= 1}
              onClick={() => handlePageChange(pagination.page - 1)}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <span className="activity-page-info">
              {pagination.page} / {pagination.totalPages}
            </span>
            <button
              className="activity-page-btn"
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => handlePageChange(pagination.page + 1)}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default PlatformActivitySection;
